package com.appsyde.contract.clearText

/**
 * Nettoie un email inbound pour l'IA / affichage compact :
 * - garde uniquement le "dernier message" (réponse du client)
 * - supprime l'historique quoted (">", "On ... wrote:", "Le ... a écrit :", etc.)
 * - supprime séparateurs type "-----Original Message-----"
 * - supprime headers typiques des blocs cités ("From:", "To:", "Subject:", "Date:")
 * - supprime signatures courantes (conservateur)
 *
 * Objectif : réduire le bruit + tokens.
 *
 * ⚠️ À utiliser sur le BODY brut (dto.body), pas sur des summaries.
 */
object EmailBodyCleaner {

    fun cleanInboundBodyForAi(raw: String, maxChars: Int = 12_000): String {
        if (raw.isBlank()) return ""

        val normalized = raw
            .replace("\r\n", "\n")
            .replace("\r", "\n")
            .trim()

        if (normalized.isBlank()) return ""

        val lines = normalized.lines()

        val cutoffIndex = lines.indexOfFirst { line ->
            isStartOfQuotedBlock(line)
        }

        val head = if (cutoffIndex >= 0) lines.take(cutoffIndex) else lines

        val compactNoSig = head.joinToString("\n").trim()
        val (bodyOnly, _) = splitBodyAndSignature(compactNoSig)
        val compact = bodyOnly.trim()
        return compact.take(maxChars).trim()
    }

    /**
     * Pour l'affichage UI : retourne le corps sans signature et la signature retirée (si détectée).
     * Premier élément = corps compacté (paragraphes) pour affichage, second = signature à afficher au clic sur « … ».
     */
    fun cleanInboundBodyAndSignatureForDisplay(raw: String, maxChars: Int = 12_000): Pair<String, String?> {
        if (raw.isBlank()) return "" to null
        val normalized = raw.replace("\r\n", "\n").replace("\r", "\n").trim()
        if (normalized.isBlank()) return "" to null
        val lines = normalized.lines()
        val cutoffIndex = lines.indexOfFirst { isStartOfQuotedBlock(it) }
        val head = if (cutoffIndex >= 0) lines.take(cutoffIndex) else lines
        val compactNoSig = head.joinToString("\n").trim()
        val (bodyOnly, signature) = splitBodyAndSignature(compactNoSig)
        val displayBody = compactParagraphsForDisplay(bodyOnly.trim()).take(maxChars).trim()
        val signatureTrimmed = signature?.trim()?.takeIf { it.isNotBlank() }
        return displayBody to signatureTrimmed
    }

    private fun isStartOfQuotedBlock(line: String): Boolean {
        val l = line.trim()
        if (l.isBlank()) return false

        // 1) quoted lines
        if (l.startsWith(">")) return true

        // 2) common separators / forwarded markers
        if (l.contains("-----Original Message-----", ignoreCase = true)) return true
        if (l.contains("-------- Forwarded message --------", ignoreCase = true)) return true
        if (l.contains("Begin forwarded message:", ignoreCase = true)) return true

        // 3) "On ... wrote:" (EN) / "Le ... a écrit :" (FR)
        //    -> on évite les regex trop strictes / trop gourmandes
        if (looksLikeOnWrote(l)) return true
        if (looksLikeLeAEcrit(l)) return true

        // 4) quoted headers block in many clients
        if (looksLikeHeaderLine(l)) return true

        return false
    }

    private fun looksLikeOnWrote(l: String): Boolean {
        // examples:
        // "On Thu, Jan 22, 2026 at 9:49 PM <x@y> wrote:"
        val lower = l.lowercase()
        if (!lower.startsWith("on ")) return false
        if (!lower.contains(" wrote")) return false
        return lower.endsWith("wrote:") || lower.endsWith("wrote :")
    }

    private fun looksLikeLeAEcrit(l: String): Boolean {
        // examples:
        // "Le jeu. 22 janv. 2026 à 21:49, X <x@y> a écrit :"
        val lower = l.lowercase()
        if (!lower.startsWith("le ")) return false
        if (!lower.contains(" a écrit")) return false
        return lower.endsWith("a écrit :") || lower.endsWith("a écrit:")
    }

    private fun looksLikeHeaderLine(l: String): Boolean {
        // examples:
        // "From: ...", "De : ...", "To: ...", "À : ...", "Subject: ...", "Objet : ...", "Date: ..."
        val lower = l.lowercase()

        fun startsWithAny(vararg prefixes: String): Boolean =
            prefixes.any { p -> lower.startsWith(p) }

        return startsWithAny(
            "from:", "from :",
            "de:", "de :",
            "to:", "to :",
            "à:", "à :",
            "cc:", "cc :",
            "subject:", "subject :",
            "objet:", "objet :",
            "date:", "date :",
            "sent:", "sent :",
            "envoyé:", "envoyé :"
        )
    }

    /** Retourne (corps sans signature, signature ou null). */
    private fun splitBodyAndSignature(text: String): Pair<String, String> {
        if (text.isBlank()) return text to ""

        val separators = listOf(
            "\n--\n",
            "\n-- \n",
            "\n—\n",
            "\nSent from my",
            "\nEnvoyé depuis",
        )
        val idxSep = separators
            .map { sep -> text.indexOf(sep, ignoreCase = true) }
            .filter { it >= 0 }
            .minOrNull()
        if (idxSep != null) {
            val before = text.substring(0, idxSep).trim()
            val after = text.substring(idxSep).trim()
            return before to after
        }

        val lines = text.replace("\r\n", "\n").replace("\r", "\n").lines()
        if (lines.size < 4) return text to ""
        val lastNonBlankIdx = lines.indexOfLast { it.trim().isNotEmpty() }
        if (lastNonBlankIdx < 0) return text to ""
        val scanFrom = maxOf(0, lastNonBlankIdx - 14)
        val tail = lines.subList(scanFrom, lastNonBlankIdx + 1)
        val closingIdxInTail = tail.indexOfLast { isClosingFormula(it.trim()) }
        if (closingIdxInTail < 0) return text to ""
        val after = tail.drop(closingIdxInTail + 1).dropWhile { it.trim().isEmpty() }
        if (after.isEmpty()) return text to ""
        val probe = after.take(6)
        val hasContactMarker = probe.any { isEmailLine(it) || isPhoneLine(it) }
        if (!hasContactMarker) return text to ""
        val cutAtGlobal = scanFrom + closingIdxInTail + 1
        val kept = lines.take(cutAtGlobal).joinToString("\n").trimEnd()
        val signaturePart = lines.drop(cutAtGlobal).joinToString("\n").trim()
        return kept to signaturePart
    }

    private fun stripSignatureConservative(text: String): String = splitBodyAndSignature(text).first

    private fun isClosingFormula(line: String): Boolean {
        if (line.isBlank()) return false
        val l = line.lowercase().trim().trimEnd(':')
        // Mets une liste courte (conservateur)
        return l == "cordialement," || l == "cordialement" ||
                l == "bien cordialement," || l == "bien cordialement" ||
                l == "bien à vous," || l == "bien à vous" ||
                l == "bonne journée," || l == "bonne journée" ||
                l == "merci," || l == "merci" ||
                l == "sincèrement," || l == "sincèrement" ||
                l == "salutations," || l == "salutations"
    }

    private val EMAIL_REGEX =
        Regex("""[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}""", RegexOption.IGNORE_CASE)

    private fun isEmailLine(line: String): Boolean =
        EMAIL_REGEX.containsMatchIn(line.trim())

    private fun isPhoneLine(line: String): Boolean {
        val s = line.trim()
        // Heuristique simple FR + intl, évite d'être trop permissif
        // Ex: 06 47 28 91 53 / +33 6 47 28 91 53 / 0647289153
        val digits = s.count { it.isDigit() }
        if (digits < 9) return false
        val hasTypicalPrefix = s.contains("+") || s.startsWith("0") || s.contains("33")
        return hasTypicalPrefix
    }

    /**
     * Optimise l'affichage du corps client (texte déjà clean) :
     * - Garde les vrais paragraphes (\n\n)
     * - Compresse les \n isolés (retours à la ligne de mise en forme) en espace
     * - Limite les lignes vides consécutives (\n{3,} → \n\n)
     * À appliquer après [cleanInboundBodyForAi] pour l'affichage UI.
     */
    fun compactParagraphsForDisplay(text: String): String {
        if (text.isBlank()) return text
        val normalized = text
            .replace(Regex("\n{3,}"), "\n\n")
            .trim()
        if (normalized.isBlank()) return ""
        val paragraphs = normalized.split(Regex("\n\n+"))
        return paragraphs
            .map { p ->
                p.replace("\n", " ")
                    .trim()
                    .replace(Regex("  +"), " ")
            }
            .filter { it.isNotEmpty() }
            .joinToString("\n\n")
    }
}
 fun repairJsonCommonMistakes(input: String): String {
    val s = input
    val out = StringBuilder(s.length)

    var inString = false
    var escape = false
    var i = 0

    fun nextNonWsIdx(from: Int): Int? {
        var j = from
        while (j < s.length) {
            if (!s[j].isWhitespace()) return j
            j++
        }
        return null
    }

    while (i < s.length) {
        val c = s[i]

        if (escape) {
            out.append(c)
            escape = false
            i++
            continue
        }

        if (inString && c == '\\') {
            out.append(c)
            escape = true
            i++
            continue
        }

        if (c == '"') {
            if (!inString) {
                inString = true
                out.append('"')
                i++
                continue
            }

            // inString: on regarde le prochain non-ws
            val j1 = nextNonWsIdx(i + 1)
            val next1 = j1?.let { s[it] }

            // fermeture normale
            val isClosing = next1 == null || next1 == ',' || next1 == '}' || next1 == ']' || next1 == ':'
            if (isClosing) {
                inString = false
                out.append('"')
                i++
                continue
            }

            // pattern "" avant délimiteur: on garde 1 quote et on supprime le suivant
            if (next1 == '"') {
                val j2 = nextNonWsIdx((j1 ?: (i + 1)) + 1)
                val next2 = j2?.let { s[it] }
                val secondIsClosing = next2 == null || next2 == ',' || next2 == '}' || next2 == ']' || next2 == ':'
                if (secondIsClosing) {
                    inString = false
                    out.append('"')
                    // saute le prochain quote
                    i = (j1 ?: (i + 1)) + 1
                    continue
                }
            }

            // sinon quote parasite
            out.append('\'')
            i++
            continue
        }

        out.append(c)
        i++
    }

    return out.toString()
}
